const API_BASE = import.meta.env.VITE_API_BASE_URL || "/api/v1";
let csrfToken = null;
let sessionPromise = null;

export class AuthRequiredError extends Error {
  constructor(message = "Debes iniciar sesión.") { super(message); this.name = "AuthRequiredError"; }
}

async function readPayload(response) {
  return response.json().catch(() => ({ error: { message: "Respuesta no valida" } }));
}

async function establishSession() {
  let response = await fetch(`${API_BASE}/auth/session`, { credentials: "include" });
  if (response.status === 401 && import.meta.env.DEV) {
    response = await fetch(`${API_BASE}/auth/dev-session`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: "{}"
    });
  }
  const payload = await readPayload(response);
  if (response.status === 401) throw new AuthRequiredError();
  if (!response.ok) throw new Error(payload.error?.message || "No se pudo iniciar la sesion");
  csrfToken = payload.csrfToken;
  return payload;
}

export async function loginSession(email, password) {
  const response = await fetch(`${API_BASE}/auth/login`, {
    method: "POST", credentials: "include", headers: { "content-type": "application/json" },
    body: JSON.stringify({ email, password })
  });
  const payload = await readPayload(response);
  if (!response.ok) throw new Error(payload.error?.message || "No se pudo iniciar la sesión");
  csrfToken = payload.csrfToken;
  sessionPromise = Promise.resolve(payload);
  return payload;
}

export function ensureSession() {
  if (!sessionPromise) sessionPromise = establishSession().catch((error) => {
    sessionPromise = null;
    throw error;
  });
  return sessionPromise;
}

export async function apiRequest(path, options = {}, retry = true) {
  if (!path.startsWith("/status") && !path.startsWith("/auth/")) await ensureSession();
  const method = options.method || "GET";
  const response = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    headers: {
      "content-type": "application/json",
      ...(method !== "GET" && method !== "HEAD" && csrfToken ? { "x-csrf-token": csrfToken } : {}),
      ...options.headers
    },
    ...options
  });
  if (response.status === 401 && retry && !path.startsWith("/auth/")) {
    sessionPromise = null;
    await ensureSession();
    return apiRequest(path, options, false);
  }
  const payload = await readPayload(response);
  if (!response.ok) throw new Error(payload.error?.message || "No se pudo completar la solicitud");
  return payload;
}

export const getPlatformStatus = () => apiRequest("/status");
export const getProjects = () => apiRequest("/projects");
export const getDashboard = () => apiRequest("/dashboard");
export const createProject = (input) => apiRequest("/projects", { method: "POST", body: JSON.stringify(input) });
export const getProjectReview = (projectId) => apiRequest(`/projects/${encodeURIComponent(projectId)}/review`);
export const getEditorialIntelligence = (projectId) => apiRequest(`/projects/${encodeURIComponent(projectId)}/editorial-intelligence`);
export const runEditorialIntelligence = (projectId, objective = "client-conversion", profile = "standard") => apiRequest(`/projects/${encodeURIComponent(projectId)}/editorial-intelligence`, { method: "POST", body: JSON.stringify({ objective, profile }) });
export const setProjectReviewState = (projectId, status) => apiRequest(`/projects/${encodeURIComponent(projectId)}/review`, { method: "PATCH", body: JSON.stringify({ status }) });
export const getLatestRevision = (projectId, candidateId) => apiRequest(`/projects/${encodeURIComponent(projectId)}/candidates/${encodeURIComponent(candidateId)}/revision`);
export const getEditorialRevisionHistory = (projectId, candidateId) => apiRequest(`/projects/${encodeURIComponent(projectId)}/candidates/${encodeURIComponent(candidateId)}/revisions`);
export const createEditorialRevision = (projectId, candidateId, settings) => apiRequest(`/projects/${encodeURIComponent(projectId)}/candidates/${encodeURIComponent(candidateId)}/revisions`, { method: "POST", body: JSON.stringify(settings) });
export const queueEditorialRender = (projectId, candidateId, revisionId) => apiRequest(`/projects/${encodeURIComponent(projectId)}/candidates/${encodeURIComponent(candidateId)}/render`, { method: "POST", body: JSON.stringify({ revisionId }) });
export const queueEditorialBatch = (projectId, items) => apiRequest(`/projects/${encodeURIComponent(projectId)}/render-batch`, { method: "POST", body: JSON.stringify({ items }) });
export const decideEditorialRevision = (projectId, candidateId, revisionId, input) => apiRequest(`/projects/${encodeURIComponent(projectId)}/candidates/${encodeURIComponent(candidateId)}/revisions/${encodeURIComponent(revisionId)}/decision`, { method: "POST", body: JSON.stringify(input) });

export async function uploadSource(projectId, file, onProgress = () => {}) {
  const started = await apiRequest(`/projects/${projectId}/uploads`, {
    method: "POST",
    body: JSON.stringify({ filename: file.name, contentType: file.type || "video/mp4", sizeBytes: file.size })
  });
  const { upload } = started;
  const parts = [];
  try {
    for (let partNumber = 1; partNumber <= upload.partsCount; partNumber += 1) {
      const start = (partNumber - 1) * upload.partSizeBytes;
      const chunk = file.slice(start, Math.min(start + upload.partSizeBytes, file.size));
      const signed = await apiRequest(`/uploads/${upload.id}/parts/${partNumber}`, { method: "POST", body: "{}" });
      const response = await fetch(signed.part.uploadUrl, { method: "PUT", body: chunk });
      if (!response.ok) throw new Error(`No se pudo subir la parte ${partNumber}`);
      const etag = response.headers.get("etag");
      if (!etag) throw new Error("El almacenamiento no devolvio ETag; revise CORS");
      parts.push({ partNumber, etag });
      onProgress(Math.round((partNumber / upload.partsCount) * 100));
    }
    return apiRequest(`/uploads/${upload.id}/complete`, {
      method: "POST",
      body: JSON.stringify({ parts })
    });
  } catch (error) {
    await apiRequest(`/uploads/${upload.id}`, { method: "DELETE" }).catch(() => {});
    throw error;
  }
}
